// Widget search for the Italian pages. Same scoring idea as
// helpers/widgetSearch.ts (name, then category, then description), with
// four changes for Italian visitors:
// - accents are folded, so "novità", "novita" and "Novità" all match
//   (the English tokenizer splits words on any non a-z letter),
// - Italian words are mapped to the English words in widget names, so
//   "modulo di contatto" finds Contact Form, whose name stays English,
// - Italian plurals (-i, -e, -chi, -ghi, -che) are reduced to a known
//   singular, see singular(); multi-word terms ("conto alla rovescia",
//   "carta da grattare") are covered by their distinctive words
//   ("rovescia", "grattare", "carta"),
// - a typed word in a name outweighs a mapped one (see expand()).
// Searches both the shown name and the English name.
// Client-safe: imports no data, so the Italian copy is not bundled.

export interface IEsSearchable {
  name: string;
  enName: string;
  teaser: string;
  categories: string[];
  order: number;
}

// Italian query word (singular) -> English words in widget names. Matched
// on whole, folded words (no accents). Plurals are covered by singular().
const IT_TO_EN: Record<string, string[]> = {
  // Reviews and trust
  recensione: ["review", "reviews", "testimonials"],
  valutazione: ["rating", "review", "reviews"],
  stella: ["review", "rating"],
  testimonianza: ["testimonials"],
  opinione: ["review", "testimonials", "sentiment"],
  cliente: ["testimonials", "review"],
  fiducia: ["trust", "badges"],
  badge: ["badge", "badges", "trust"],
  // Forms and bookings
  modulo: ["form"],
  contatto: ["contact", "form"],
  richiesta: ["contact", "form", "support"],
  iscrizione: ["registration", "subscription", "form"],
  registrazione: ["registration", "form"],
  prenotazione: ["booking", "appointment"],
  prenota: ["booking", "appointment"],
  appuntamento: ["appointment", "booking"],
  calendario: ["calendar"],
  evento: ["event", "calendar"],
  consenso: ["consent", "cookie"],
  ordine: ["order"],
  candidatura: ["job", "application"],
  petizione: ["petition"],
  donazione: ["donation", "donate"],
  assistenza: ["support"],
  newsletter: ["subscription", "email"],
  // Timers and counters
  countdown: ["countdown"],
  rovescia: ["countdown"],
  conto: ["countdown", "count"],
  cronometro: ["timer"],
  contatore: ["counter", "countup"],
  visitatore: ["visitor"],
  visita: ["visitor", "tour"],
  numero: ["number", "counter"],
  statistica: ["number", "counter", "charts"],
  orologio: ["clock"],
  fuso: ["clock"],
  orario: ["hours", "clock"],
  apertura: ["hours"],
  meteo: ["weather"],
  previsione: ["weather", "forecast"],
  // Images, video and audio
  carosello: ["carousel"],
  presentazione: ["slideshow", "slider", "carousel"],
  galleria: ["gallery"],
  foto: ["image", "gallery"],
  immagine: ["image"],
  muro: ["wall"],
  griglia: ["grid"],
  lente: ["magnifier"],
  ingrandimento: ["magnifier"],
  musica: ["music", "audio"],
  lettore: ["player"],
  dispositivo: ["device", "mockup"],
  animazione: ["animated", "lottie"],
  animato: ["animated"],
  virtuale: ["virtual", "tour"],
  panorama: ["virtual", "tour"],
  sfogliabile: ["flipbook"],
  libro: ["flipbook"],
  prima: ["before"],
  dopo: ["after", "before"],
  confronto: ["comparison", "before"],
  // Popups, bars and buttons
  pulsante: ["button"],
  bottone: ["button"],
  tasto: ["button"],
  barra: ["bar"],
  banner: ["banner", "bar"],
  finestra: ["popup"],
  avanzamento: ["progress"],
  progresso: ["progress"],
  notifica: ["notification"],
  avviso: ["notification", "notice", "cookie"],
  annuncio: ["announcements", "notification", "listings"],
  novita: ["news", "announcements", "new"],
  notizia: ["news", "announcements"],
  scorrimento: ["scroll", "ticker"],
  scorrevole: ["ticker", "slider", "scroll"],
  cima: ["top"],
  sconto: ["coupon"],
  promozione: ["coupon", "marketing"],
  promo: ["coupon"],
  telefono: ["call"],
  chiamata: ["call"],
  // Sales and payments
  prezzo: ["pricing"],
  tariffa: ["pricing"],
  listino: ["pricing"],
  tabella: ["table", "tables"],
  pagamento: ["payment", "paypal", "stripe"],
  abbonamento: ["subscription"],
  prodotto: ["product", "catalog"],
  catalogo: ["catalog"],
  negozio: ["catalog", "product"],
  vendita: ["product", "catalog", "coupon"],
  valuta: ["currency"],
  convertitore: ["converter", "currency"],
  cambio: ["currency", "converter"],
  taglia: ["size"],
  immobiliare: ["real", "estate"],
  immobile: ["real", "estate"],
  // Content and layout
  domanda: ["faq"],
  risposta: ["faq"],
  fisarmonica: ["accordion"],
  comprimibile: ["collapsible", "accordion"],
  scheda: ["tabs", "card"],
  carta: ["card"],
  mappa: ["map", "maps"],
  posizione: ["map", "maps"],
  indirizzo: ["map", "maps"],
  sede: ["branch", "map"],
  attivita: ["business"],
  azienda: ["business", "branch"],
  elenco: ["list", "listings"],
  lista: ["list", "listings"],
  cronologia: ["timeline"],
  citazione: ["quotes"],
  titolo: ["headline"],
  grafico: ["charts"],
  diagramma: ["diagrams", "charts"],
  squadra: ["team"],
  membro: ["team", "member"],
  lavoro: ["job"],
  corso: ["course"],
  glossario: ["glossary"],
  collegamento: ["link", "links"],
  ristorante: ["restaurant"],
  competenza: ["skill"],
  codice: ["code", "qr"],
  processo: ["process"],
  passaggio: ["process"],
  // Games and engagement
  sondaggio: ["poll", "survey"],
  voto: ["votes", "voting", "poll"],
  votazione: ["voting", "votes", "poll"],
  gioco: ["game", "quiz"],
  personalita: ["personality"],
  concorso: ["contest", "brackets"],
  torneo: ["bracket", "tournament"],
  classifica: ["leaderboard", "tier", "ranking", "scores"],
  punteggio: ["scores", "leaderboard"],
  ruota: ["wheel"],
  fortuna: ["wheel"],
  gratta: ["scratch"],
  grattare: ["scratch"],
  girevole: ["flip"],
  commento: ["comments"],
  // Social and chat
  condividi: ["share"],
  condivisione: ["share"],
  post: ["feed", "posts"],
  conversazione: ["conversation", "chat"],
  messaggio: ["chat", "conversation", "messenger"],
  // Tools
  verifica: ["verification", "gate"],
  eta: ["verification", "gate"],
  traduzione: ["translator"],
  traduttore: ["translator"],
  lingua: ["translator"],
  ricerca: ["search"],
  cerca: ["search"],
  accessibilita: ["accessibility"],
  calcolatrice: ["calculator"],
  calcolatore: ["calculator"],
  caloria: ["tdee", "calculator"],
  blocco: ["adblocker"],
  clic: ["click"],
  destro: ["right"],
  protezione: ["protection", "right"],
};

// Lowercase and drop accents ("novità" -> "novita", "età" -> "eta").
const fold = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Italian names carry "e", "di", "per" and the like ("Recensioni e
// fiducia"), and short words match inside longer ones ("con" in "contact"),
// so these are dropped. Elided "l'", "un'" are dropped by the length filter
// in tokenize().
const STOPWORDS = new Set([
  "il", "lo", "la", "gli", "le", "un", "uno", "una", "di", "da", "del",
  "dello", "della", "dei", "degli", "delle", "al", "allo", "alla", "ai",
  "agli", "alle", "dal", "dalla", "nel", "nella", "sul", "sulla", "ed",
  "per", "con", "su", "in", "tra", "fra", "mio", "mia", "miei", "mie",
  "tuo", "tua", "tuoi", "tue", "suo", "sua", "che", "come", "aggiungere",
  "aggiungi", "inserire", "inserisci", "widget", "widgets", "sito", "siti",
  "web", "internet", "pagina", "pagine", "gratis", "gratuito",
]);

const tokenize = (text: string) =>
  fold(text)
    .split(/[^a-z0-9&]+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));

// Folded Italian plurals back to a known singular: "recensioni" ->
// "recensione", "moduli" -> "modulo", "orari" -> "orario", "gallerie" ->
// "galleria", "giochi" -> "gioco", "cataloghi" -> "catalogo", "notifiche" ->
// "notifica". Only returns a candidate that is a known word, except a final
// "s" (English plurals like "reviews"), which is always stripped.
const singular = (w: string) => {
  if (IT_TO_EN[w]) return w;
  const stem = (n: number) => w.slice(0, -n);
  const candidates: string[] = [];
  if (w.endsWith("chi")) candidates.push(`${stem(3)}co`);
  if (w.endsWith("ghi")) candidates.push(`${stem(3)}go`);
  if (w.endsWith("che")) candidates.push(`${stem(3)}ca`);
  if (w.endsWith("ghe")) candidates.push(`${stem(3)}ga`);
  if (w.endsWith("i"))
    candidates.push(`${stem(1)}o`, `${stem(1)}e`, `${stem(1)}io`, `${stem(1)}a`);
  if (w.endsWith("e")) candidates.push(`${stem(1)}a`);
  if (w.endsWith("s")) candidates.push(stem(1));
  const known = candidates.find((c) => c.length > 2 && IT_TO_EN[c]);
  if (known) return known;
  if (w.length > 3 && w.endsWith("s")) return stem(1);
  return w;
};

// Words the visitor typed, and the English words they map to. A typed word
// found in a name counts more than a mapped one, so "pulsante whatsapp" ranks
// the WhatsApp widget above every other button.
function expand(query: string) {
  const words = tokenize(query);
  const typed = Array.from(new Set([...words, ...words.map(singular)]));
  const mapped = words
    .flatMap((w) => IT_TO_EN[w] || IT_TO_EN[singular(w)] || [])
    .filter((w) => !typed.includes(w));
  return { typed, mapped: Array.from(new Set(mapped)) };
}

export function searchWidgetsIt<T extends IEsSearchable>(
  widgets: T[],
  query: string
): T[] {
  const { typed, mapped } = expand(query);
  if (!typed.length) return widgets;
  return widgets
    .map((w) => {
      const name = fold(`${w.name} ${w.enName}`);
      const cats = fold(w.categories.join(" "));
      const teaser = fold(w.teaser);
      let score = 0;
      typed.forEach((t) => {
        if (name.includes(t)) score += 6;
        if (cats.includes(t)) score += 2;
        if (teaser.includes(t)) score += 1;
      });
      mapped.forEach((t) => {
        if (name.includes(t)) score += 4;
        if (cats.includes(t)) score += 2;
        if (teaser.includes(t)) score += 1;
      });
      return { w, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.w.order - b.w.order)
    .map(({ w }) => w);
}
